import React, { memo, useCallback } from 'react';
import { styled } from '@compiled/react';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import {
	InlineCreate as InlineCreateComponent,
	type InlineCreateProps,
} from '@atlassian/jira-business-issue-create';
import { CARD_GUTTER, COLUMN_RADIUS } from '../../../../common/constants';
import type { BoardColumn } from '../../../../common/types';
import { useInlineCreateProps } from '../../../../controllers/inline-create-old';
import type { CreatedBoardItem } from '../../../../services/create-issue';

const inlineCreateStyles: InlineCreateProps<CreatedBoardItem>['inlineCreateStyles'] = {
	borderRadius: token('border.radius', '3px'),
};

type Props = {
	column: BoardColumn;
	/**
	 * This is marked as optional but is expected to be present
	 * at all times. This prop is injected into the component
	 * using cloneElement.
	 *
	 * See: https://stash.atlassian.com/projects/JIRACLOUD/repos/jira-frontend/browse/src/packages/business/board/src/ui/board/column/virtual-cards/index.tsx?at=51e3fc90aff
	 */
	scrollToLastElement?: () => void;
};

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

const InlineCreate = ({ column, scrollToLastElement = noop }: Props) => {
	const onAddOptimisticIssue = useCallback(() => {
		// Only attempt to scroll to the last element after the optimistic issue is created
		requestAnimationFrame(scrollToLastElement);
	}, [scrollToLastElement]);

	const inlineCreateProps = useInlineCreateProps({
		column,
		onAddOptimisticIssue,
	});

	const handleToggleForm = useCallback(
		(isFormVisible: boolean) => {
			if (isFormVisible) {
				scrollToLastElement();
			}
		},
		[scrollToLastElement],
	);

	return (
		<Wrapper>
			<InlineCreateComponent
				{...inlineCreateProps}
				location="boardFooter"
				inlineCreateStyles={inlineCreateStyles}
				onToggleForm={handleToggleForm}
			/>
		</Wrapper>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('elevation.surface.sunken', colors.N20),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `0 0 ${COLUMN_RADIUS}px ${COLUMN_RADIUS}px`,
	bottom: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	margin: `auto -${CARD_GUTTER}px -${CARD_GUTTER}px`,
	padding: `${token('space.050', '4px')} 5px`,
	position: 'sticky',
	zIndex: 1,
});

export default memo(InlineCreate);
